// ======================================
// History API-based SPA Router (Clean URLs)
// ======================================

class Router {
  constructor() {
    this.routes = {};
    this.currentPage = null;

    // Hardcode base path for ROOT deployment
    // Change to '/NVI/' if deploying in subdirectory
    this.basePath = '/';

    // Listen for auth:unauthorized event
    // Listen for auth:unauthorized event (Deprecated - handled directly in api.js)
    // window.addEventListener('auth:unauthorized', () => {
    //   this.navigate('/login');
    // });

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', () => this.handleRoute());

    // Intercept all link clicks for SPA navigation
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.href && link.href.startsWith(window.location.origin)) {
        const href = link.getAttribute('href');
        // Ignore external links, mailto, etc handled by browser
        if (link.target === '_blank') return;

        e.preventDefault();

        // Handle path extraction
        let path = href;

        // If it's a full URL, extract pathname
        if (href.startsWith('http')) {
          const url = new URL(href);
          path = url.pathname;
        }

        this.navigate(path);
      }
    });
  }

  // Register a route
  route(path, handler) {
    this.routes[path] = handler;
    return this;
  }

  // Navigate to a route
  navigate(path) {
    // 1. Strip base path if present in input (so we have pure logical path)
    if (this.basePath && this.basePath !== '/' && path.startsWith(this.basePath)) {
      path = path.slice(this.basePath.length);
    }

    // Ensure logical path starts with /
    if (!path.startsWith('/')) path = '/' + path;

    // 2. Construct full path for browser URL
    let targetUrl = path;
    if (this.basePath && this.basePath !== '/') {
      // Ensure basePath doesn't have trailing slash if path has leading slash
      const base = this.basePath.endsWith('/') ? this.basePath.slice(0, -1) : this.basePath;
      targetUrl = base + path;
    }

    if (window.location.pathname !== targetUrl) {
      window.history.pushState({}, '', targetUrl);
    }
    this.handleRoute();
  }

  // Handle route changes
  handleRoute() {
    let pathname = window.location.pathname;

    // Strip trailing slash if not root
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }

    // Handle Subdirectory deployment:
    // If the app is deployed at /sub/dir, and we want to match route /admin,
    // we need to strip /sub/dir from pathname.
    // We can try to match the longest route key that matches the end of the pathname.

    let matchedRoute = null;
    let matchedHandler = null;
    let routeParams = {};

    // 1. Exact match attempt with full pathname
    if (this.routes[pathname]) {
      matchedHandler = this.routes[pathname];
      matchedRoute = pathname;
    }
    // 2. Exact match attempt by stripping known segments? 
    // Better: Iterate identifying which registered route matches the SUFFIX of the current pathname
    else {
      for (const [route, handler] of Object.entries(this.routes)) {
        // Case 1: Static Route (e.g. /admin derived from /NVI/admin)
        if (pathname.endsWith(route)) {
          // Check if the prefix is just a base path
          const prefix = pathname.slice(0, pathname.length - route.length);
          // valid prefix is either empty or ends with / 
          // (or we are at root and route is /)
          if (prefix === '' || prefix === '/' || (prefix.startsWith('/') && route.startsWith('/'))) {
            // This is a weak check but works for common cases like /app/admin vs /admin
            // Verify stricter: 
            // if route is '/', it matches everything ending in /, which is risky.
            if (route === '/' && pathname !== '/' && pathname.split('/').length > 2) continue;

            matchedHandler = handler;
            matchedRoute = route;
            break;
          }
        }
      }
    }

    // 3. Dynamic route matching (e.g., /projects/:slug)
    if (!matchedHandler) {
      for (const [route, routeHandler] of Object.entries(this.routes)) {
        if (!route.includes(':')) continue; // Skip static routes checked above

        const routeParts = route.split('/').filter(Boolean);
        const pathParts = pathname.split('/').filter(Boolean);

        // We might have extra prefix parts in pathParts due to subdirectory
        // e.g. path: [NVI, projects, 123], route: [projects, :slug]

        if (pathParts.length >= routeParts.length) {
          // Check if the END of pathParts matches routeParts structure
          const offset = pathParts.length - routeParts.length;
          let match = true;
          const params = {};

          for (let i = 0; i < routeParts.length; i++) {
            const pathPart = pathParts[offset + i];
            const routePart = routeParts[i];

            if (routePart.startsWith(':')) {
              params[routePart.slice(1)] = pathPart;
            } else if (routePart !== pathPart) {
              match = false;
              break;
            }
          }

          if (match) {
            matchedHandler = routeHandler;
            routeParams = params;
            break;
          }
        }
      }
    }

    // Execute handler or show 404
    if (matchedHandler) {
      this.currentPage = matchedRoute || pathname; // approximate
      matchedHandler(routeParams);
      this.updateActiveNav();
      window.scrollTo(0, 0);
    } else {
      console.log('404 Not Found:', pathname);
      console.log('Available Routes:', Object.keys(this.routes));
      this.show404();
    }
  }

  // Update active navigation state
  updateActiveNav() {
    const navLinks = document.querySelectorAll('.nav-link, .mobile-nav-link');
    const currentPath = window.location.pathname;

    navLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) return;

      // Simple strict check or check if current path ends with href
      if (href === '/' && (currentPath === '/' || currentPath === '' || currentPath.endsWith('/index.html'))) {
        link.classList.add('active');
      }
      else if (href !== '/' && currentPath.includes(href)) {
        link.classList.add('active');
      } else {
        link.classList.remove('active');
      }
    });
  }

  // Show 404 page
  show404() {
    const app = document.getElementById('app');
    app.innerHTML = `
      <div class="container section" style="text-align: center; min-height: 60vh; display: flex; flex-direction: column; justify-content: center;">
        <h1 class="text-5xl font-bold mb-4">404</h1>
        <p class="text-lg text-muted mb-8">Page not found</p>
        <p class="text-sm text-muted">Requested: ${window.location.pathname}</p>
        <a href="./" class="btn btn-primary" style="align-self: center;">Go Home</a>
      </div>
    `;
  }
}

// Create and export router instance
export const router = new Router();
